import { useEffect, useCallback, useRef } from "react";
import { supabase } from "@/shared/integrations/supabase/client";
import { useAuth } from "@/shared/hooks/useAuth";

const logActivityDirect = async (userId: string, userName: string, module: string, action: string, details?: string) => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.user?.id || session.user.id !== userId) return;

    await supabase.from("activity_logs").insert({
      user_id: userId,
      user_name: userName,
      module,
      action,
      details: details || null,
    });
  } catch (err) {
    console.error("[ActivityLog] Failed:", err);
  }
};

const SESSION_KEY = "ferrugini_session_id";
const LOGIN_FLAG_KEY = "ferrugini_logged_in";

export const useSessionTracking = () => {
  const { user } = useAuth();
  const tokenRef = useRef<string | null>(null);

  const getSessionId = () => sessionStorage.getItem(SESSION_KEY);
  const setSessionId = (id: string | null) => {
    if (id) sessionStorage.setItem(SESSION_KEY, id);
    else sessionStorage.removeItem(SESSION_KEY);
  };

  const recordLogout = useCallback(async (reason: string) => {
    const sid = getSessionId();
    if (!sid) return;
    try {
      await supabase
        .from("user_sessions")
        .update({ logout_at: new Date().toISOString(), logout_reason: reason })
        .eq("id", sid);
      setSessionId(null);
      sessionStorage.removeItem(LOGIN_FLAG_KEY);
    } catch (err) {
      console.error("[SessionTracking] recordLogout failed:", err);
    }
  }, []);

  // Login — runs once per browser session per user
  useEffect(() => {
    if (!user?.id) return;

    const userId = user.id;
    const alreadyLogged = sessionStorage.getItem(LOGIN_FLAG_KEY);
    if (alreadyLogged === userId) return;

    let cancelled = false;
    sessionStorage.setItem(LOGIN_FLAG_KEY, userId);

    const isAuthenticated = async (): Promise<boolean> => {
      if (cancelled) return false;
      const {
        data: { session },
      } = await supabase.auth.getSession();
      return session?.user?.id === userId;
    };

    const recordLoginDay = async () => {
      if (!(await isAuthenticated())) return;
      const { error } = await supabase.from("user_login_days").upsert(
        { user_id: userId, login_date: new Date().toISOString().split("T")[0] },
        { onConflict: "user_id,login_date" }
      );
      if (error && !cancelled) {
        console.warn("[SessionTracking] user_login_days:", error.message);
      }
    };

    const doLogin = async () => {
      try {
        if (!(await isAuthenticated())) return;

        // Dia de login (streak) primeiro — evita corrida com logout durante inserts lentos
        await recordLoginDay();
        if (!(await isAuthenticated())) return;

        const { data: profile } = await supabase
          .from("profiles")
          .select("nome")
          .eq("user_id", userId)
          .maybeSingle();

        if (!(await isAuthenticated())) return;

        const fiveMinutesAgo = new Date(Date.now() - 5 * 60_000).toISOString();
        const { data: recentSession } = await supabase
          .from("user_sessions")
          .select("id")
          .eq("user_id", userId)
          .eq("logout_reason", "active")
          .gte("login_at", fiveMinutesAgo)
          .limit(1);

        if (!(await isAuthenticated())) return;

        if (recentSession && recentSession.length > 0) {
          setSessionId(recentSession[0].id);
          return;
        }

        const { data } = await supabase
          .from("user_sessions")
          .insert({
            user_id: userId,
            user_name: profile?.nome || user.email || "",
            login_at: new Date().toISOString(),
            logout_reason: "active",
          })
          .select("id")
          .single();

        if (!(await isAuthenticated())) return;

        if (data) {
          setSessionId(data.id);
          await logActivityDirect(userId, profile?.nome || user.email || "", "sistema", "Login realizado");
        }
      } catch (err) {
        console.error("[SessionTracking] recordLogin failed:", err);
        sessionStorage.removeItem(LOGIN_FLAG_KEY);
      }
    };

    void doLogin();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Mantém token em ref para beforeunload (PATCH precisa de JWT do usuário, não anon key)
  useEffect(() => {
    if (!user?.id) {
      tokenRef.current = null;
      return;
    }
    supabase.auth.getSession().then(({ data: { session } }) => {
      tokenRef.current = session?.access_token ?? null;
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      tokenRef.current = session?.access_token ?? null;
    });
    return () => subscription.unsubscribe();
  }, [user?.id]);

  // Registra fechamento da aba/janela (sessão Supabase expira com sessionStorage ao fechar)
  useEffect(() => {
    if (!user?.id) return;

    const handleBeforeUnload = () => {
      const sid = getSessionId();
      const token = tokenRef.current;
      if (sid && token) {
        const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/user_sessions?id=eq.${sid}`;
        const body = JSON.stringify({ logout_at: new Date().toISOString(), logout_reason: "closed" });
        fetch(url, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            "Authorization": `Bearer ${token}`,
            "Prefer": "return=minimal",
          },
          body,
          keepalive: true,
        }).catch(() => {});
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [user?.id]);

  return { recordLogout };
};
